/**
 * What the phone says when an answer fails, and whether it says anything.
 *
 * Every failure used to read as a refused microphone. A caller who hangs up during the answer is
 * the common failure on the floor: the mic and the ICE wait take about a second, and the caller
 * or the ACD can let go inside it. The rep had done nothing wrong and their microphone was fine
 * (ACL-651).
 *
 * The vendored client removes the call from the phone on ANY failure, so none of these messages
 * can offer the call back to the rep.
 *
 * Kept out of `softphone-context.tsx` so the rule is tested without a Verto client or React, and
 * out of `vendor/`, which is ported and not ours to change.
 */

/** The cause the vendored client gives a call its own failed answer ended. */
const OWN_FAILURE = "ANSWER_FAILED";

/** What `getUserMedia` rejects with when the microphone is the problem. */
const MICROPHONE_ERRORS = new Set(["NotAllowedError", "NotFoundError", "NotReadableError"]);

/** The answer failed because the call was already over. Nothing for the rep to do about it. */
export class CallEndedWhileAnswering extends Error {
  constructor(callId: string, cause: unknown) {
    super(`Call ${callId} ended while it was being answered`, { cause });
    this.name = "CallEndedWhileAnswering";
  }
}

/** The part of the vendored client this needs. */
interface CallEvents {
  on(event: string, callback: (...args: unknown[]) => void): () => void;
}

/**
 * Run `answer`, and turn its failure into `CallEndedWhileAnswering` when the call ended under it.
 * The client's event is the only way to tell: it goes on to send `verto.answer` for a call the
 * switch has already dropped, and what comes back is an ordinary error.
 */
export async function answerWatchingTheCall(
  client: CallEvents,
  callId: string,
  answer: () => Promise<void>,
): Promise<void> {
  let ended = false;
  const stop = client.on("callEnded", (...args: unknown[]) => {
    const info = args[0] as { callId?: string; cause?: string } | undefined;
    if (info?.callId === callId && info.cause !== OWN_FAILURE) ended = true;
  });
  try {
    await answer();
  } catch (e) {
    // "Call not found": it ended before this answer started, so the event came and went unseen.
    const alreadyGone = e instanceof Error && e.message === "Call not found";
    throw ended || alreadyGone ? new CallEndedWhileAnswering(callId, e) : e;
  } finally {
    stop();
  }
}

/** The message for a failed answer. Null when there is nothing to tell the rep. */
export function answerFailureMessage(e: unknown): string | null {
  if (e instanceof CallEndedWhileAnswering) return null;
  if (e instanceof Error && MICROPHONE_ERRORS.has(e.name)) {
    return "Could not answer the call. Check that this browser may use the microphone.";
  }
  return "Could not answer the call. If this keeps happening, reload this page.";
}
